// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  IUserGetListAttrs,
  IUserGetDetailAttrs,
  IUserCreateAttrs,
  IUserUpdateAttrs,
  IUserApproveAttrs,
  IUserRejectAttrs,
  IUserDeleteAttrs,
  IUserValidateEmailAttrs,
  IUserGetDropdownAttrs,
  IUserDownloadAttrs,
} from 'interfaces/back-office/user/user-view.interface'
import { IUserValidateEmail } from 'interfaces/back-office/user'

const useUser = () => {
  // Hook
  const {
    user: {
      user_loading,
      user_list,
      user_detail,
      user_rejectForm,
      user_approveForm,
      user_dropdown,
      user_SET_REJECT_FORM,
      user_SET_APPROVE_FORM,
      user_CLEAR_LIST,
      user_CLEAR_DETAIL,
      user_CLEAR_REJECT_FORM,
      user_CLEAR_APPROVE_FORM,
      user_CLEAR_DROPDOWN,
      user_fetchList: fetchList,
      user_fetchDetail: fetchDetail,
      user_create: create,
      user_update: update,
      user_approve: approve,
      user_reject: _reject,
      user_delete: _delete,
      user_validateEmail: validateEmail,
      user_fetchDropdown: fetchDropdown,
      user_download: download,
    },
  } = useStores()

  /**
   * @description Get user list
   *
   * @param {IUserGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_fetchList = useCallback(
    async (payload: IUserGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchList],
  )

  /**
   * @description Get single user
   *
   * @param {IUserGetDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_fetchDetail = useCallback(
    async (payload: IUserGetDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchDetail],
  )

  /**
   * @description Create users
   *
   * @param {IUserCreateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_create = useCallback(
    (payload: IUserCreateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        create({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [create],
  )

  /**
   * @description Update User
   *
   * @param {IUserUpdateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_update = useCallback(
    async (payload: IUserUpdateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [update],
  )

  /**
   * @description Approve user
   *
   * @param {IUserApproveAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_approve = useCallback(
    async (payload: IUserApproveAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        approve({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [approve],
  )

  /**
   * @description Reject user
   *
   * @param {IUserRejectAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_reject = useCallback(
    async (payload: IUserRejectAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        _reject({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [_reject],
  )

  /**
   * @description Remove user
   *
   * @param {IUserDeleteAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_delete = useCallback(
    async (payload: IUserDeleteAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        _delete({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [_delete],
  )

  /**
   * @description Validate email of user
   *
   * @param {IUserValidateEmailAttrs} payload
   *
   * @return {Promise<IUserValidateEmail | void>} Promise<IUserValidateEmail | void>
   */
  const user_validateEmail = useCallback(
    async (payload: IUserValidateEmailAttrs): Promise<IUserValidateEmail | void> => {
      return new Promise((resolve, reject) => {
        validateEmail({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [validateEmail],
  )

  /**
   * @description Fetch dropdown of users
   *
   * @param {IUserGetDropdownAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_fetchDropdown = useCallback(
    async (payload: IUserGetDropdownAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDropdown({
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchDropdown],
  )

  /**
   * @description Download
   *
   * @param {IUserDownloadAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const user_download = useCallback(
    (payload: IUserDownloadAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        download({
          params: payload.params,
          requestConfig: payload.requestConfig,
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [download],
  )

  return {
    user_loading,
    user_list,
    user_detail,
    user_rejectForm,
    user_approveForm,
    user_dropdown,
    user_SET_REJECT_FORM,
    user_SET_APPROVE_FORM,
    user_CLEAR_LIST,
    user_CLEAR_DETAIL,
    user_CLEAR_REJECT_FORM,
    user_CLEAR_APPROVE_FORM,
    user_CLEAR_DROPDOWN,
    user_fetchList,
    user_fetchDetail,
    user_create,
    user_update,
    user_approve,
    user_reject,
    user_delete,
    user_validateEmail,
    user_fetchDropdown,
    user_download,
  }
}

export { useUser }
